import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTracker } from '@dpdgroupuk/react-event-tracker';
import { makeStyles } from '@mui/styles';

import RouteManagement from './RouteManagement';
import { wrapPromise } from '../../../utils/promise';
import StopList from './StopList';
import stopTypes from '../../../enums/stopTypes';
import { updateSelectedStops } from '../../../store/reducers/ui/routeDetail/stopFilters';
import { ROUTE_DETAIL } from '../../../constants/analytics';
import modals from '../../../enums/modals';
import { useModalContext } from '../../../components/ModalControlled/ModalProvider';
import { View } from '../../../components/Base';
import layoutStyles from '../../../components/styles/layout';
import useReconsolidationModalData from '../hooks/useReconsolidationModalData';

const useStyles = makeStyles(() => ({
  container: {
    zIndex: 3,
    position: 'absolute',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    width: `calc(100vw - ${layoutStyles.routeList.width}px)`,
    right: 0,
  },
  context: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
}));

const RouteDetailContainer = ({
  route,
  handleBackClick,
  availableForReConsolidation,
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const tracker = useTracker();
  const { getModalRef } = useModalContext();
  const { openReconsolidationNoticeModal } = useReconsolidationModalData();

  const handleSelectMultipleRouteStops = useCallback(async () => {
    const modalRef = getModalRef(modals.SELECT_MULTIPLE);
    const [result, canceled] = await wrapPromise(
      modalRef.current.showModal({
        routeStops: route.stop ?? [],
      })
    );

    if (canceled) {
      return;
    }

    const { from, to } = result;
    const [fromNormalized, toNormalized] = from <= to ? [from, to] : [to, from];

    dispatch(
      updateSelectedStops(
        route.stop
          .slice(fromNormalized, toNormalized + 1)
          .map(({ stopId }) => stopId)
      )
    );
  }, [route.stop, dispatch]);

  useEffect(() => {
    tracker.logEvent(ROUTE_DETAIL.LOAD);
  }, [tracker]);

  useEffect(() => {
    if (!route.routeManuallyChanged && availableForReConsolidation) {
      openReconsolidationNoticeModal(availableForReConsolidation, true);
    }
  }, [route.routeManuallyChanged, availableForReConsolidation, route.routeId]);

  const onSelectStopRequests = useCallback((stop) => {
    if (stop.type === stopTypes.shop && stop.requests.length > 1) {
      const pickupModalRef = getModalRef(modals.PICK_REQUEST);
      return wrapPromise(pickupModalRef.current.showModal());
    }
    const requestModalRef = getModalRef(modals.REQUEST);
    return wrapPromise(
      requestModalRef.current.showModal({
        stop,
      })
    );
  }, []);

  const onStopParcelsInfoClick = useCallback((stop) => {
    const parcelsInfoModalRef = getModalRef(modals.PARCELS_INFO);
    return wrapPromise(
      parcelsInfoModalRef.current.showModal({
        stop,
      })
    );
  }, []);

  return (
    <>
      <View className={styles.container}>
        <RouteManagement
          onSelectMultipleClick={handleSelectMultipleRouteStops}
          handleBackClick={handleBackClick}
          onParcelRequestClick={onSelectStopRequests}
        />
        <StopList
          onSelectStopRequests={onSelectStopRequests}
          onParcelInfoClick={onStopParcelsInfoClick}
        />
      </View>
    </>
  );
};

RouteDetailContainer.propTypes = {
  route: PropTypes.object.isRequired,
  handleBackClick: PropTypes.func.isRequired,
  availableForReConsolidation: PropTypes.bool.isRequired,
};

export default RouteDetailContainer;
